// Google Analytics 4 implementation
import { persistLandingUtm, getPersistedUtm } from '@/lib/utm';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_TRACKING_ID = 'G-WNRNEPM1C1';

/** Hostname of the referring page, or '(none)' when the browser stripped it. */
export const getReferrerHost = (): string => {
  if (typeof document === 'undefined' || !document.referrer) return '(none)';
  try {
    const host = new URL(document.referrer).hostname;
    if (typeof window !== 'undefined' && host === window.location.hostname) return '(self)';
    return host;
  } catch {
    return '(unknown)';
  }
};

const LANDING_PATH_KEY = 'brrgr_landing_path';

/** First path of the session — lets you split "homepage direct" from "deep-page direct". */
export const getLandingPath = (): string => {
  if (typeof window === 'undefined') return '(unknown)';
  try {
    const existing = window.sessionStorage.getItem(LANDING_PATH_KEY);
    if (existing) return existing;
    const path = window.location.pathname;
    window.sessionStorage.setItem(LANDING_PATH_KEY, path);
    return path;
  } catch {
    return window.location.pathname;
  }
};

// Google Analytics is loaded in index.html; this seeds session-level context.
export const initGA = () => {
  if (typeof window === 'undefined' || !window.gtag) return;

  persistLandingUtm();
  getLandingPath();

  const platform = detectPlatform();
  const isNative = platform === 'ios' || platform === 'android';

  // User properties are reportable as custom dimensions in GA4.
  window.gtag('set', 'user_properties', {
    app_platform: platform,
    surface: isNative ? 'native_app' : 'web',
  });

  // Lets you exclude native-app sessions from web acquisition reports via a
  // GA4 internal/developer traffic filter on traffic_type.
  window.gtag('set', {
    traffic_type: isNative ? 'app' : 'web',
    referrer_host: getReferrerHost(),
    landing_path: getLandingPath(),
  });
};


// Detect platform automatically
const detectPlatform = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for Capacitor native app
  if ((window as any).Capacitor?.isNativePlatform()) {
    return (window as any).Capacitor.getPlatform(); // 'ios' or 'android'
  }
  
  // Check for PWA (installed to home screen)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    if (/iphone|ipad|ipod/.test(userAgent)) return 'pwa-ios';
    if (/android/.test(userAgent)) return 'pwa-android';
    return 'pwa-desktop';
  }
  
  // Mobile web browser
  if (/iphone|ipad|ipod/.test(userAgent)) return 'web-ios';
  if (/android/.test(userAgent)) return 'web-android';
  
  return 'web-desktop';
};

// Track app install source from URL parameters + auto-detect platform
export const trackInstallSource = () => {
  if (typeof window === 'undefined') return;
  
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');
  const platform = detectPlatform();
  
  // Always track the platform, even without UTM params
  trackEvent('app_open', 'acquisition', platform);
  
  if (source || medium || campaign) {
    if (window.gtag) {
      window.gtag('event', 'campaign_details', {
        source: source || 'direct',
        medium: medium || 'none',
        campaign: campaign || 'none',
        platform: platform,
      });
    }
  }
};

// Track page views
// Sends the raw referrer, its hostname and the landing path so "(direct)"
// sessions can be broken down in GA4 Explorations (dark social, webviews, apps).
export const trackPageView = (path: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: path,
      page_title: title || document.title,
      referrer_host: getReferrerHost(),
      referrer_raw: (typeof document !== 'undefined' && document.referrer) || '(none)',
      landing_path: getLandingPath(),
      app_platform: detectPlatform(),
      ...getPersistedUtm(),
    });
  }
};


// Track events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track user signup
export const trackSignup = (method: 'email' | 'google' | 'apple' | 'magic_link') => {
  trackEvent('sign_up', 'authentication', method);
};

// Track user signin
export const trackSignin = (method: 'email' | 'google' | 'apple' | 'magic_link') => {
  trackEvent('login', 'authentication', method);
};

// Track burger actions
export const trackBurgerAdd = () => {
  trackEvent('burger_add', 'user_action', 'add_burger');
};

export const trackBurgerRate = (rating: number) => {
  trackEvent('burger_rate', 'user_action', 'rate_burger', rating);
};

export const trackBurgerView = (burgerId: string) => {
  trackEvent('burger_view', 'content_view', burgerId);
};

// Track social actions
export const trackFollow = (action: 'follow' | 'unfollow') => {
  trackEvent(`user_${action}`, 'social', action);
};

export const trackSearch = (query: string) => {
  trackEvent('search', 'user_action', query);
};

// Track invite sharing
export const trackInviteShare = (method: 'native' | 'copy' | 'fallback_copy') => {
  trackEvent('invite_share', 'virality', method);
};

// Track navigation
export const trackNavigation = (destination: string) => {
  trackEvent('navigate', 'navigation', destination);
};

// ── Onboarding funnel events ──
// Splash funnel:      splash_view → auth_start → signup_complete → first_checkin
// SEO/leaderboard:    leaderboard_view → leaderboard_cta_click → auth_start → signup_complete → first_checkin
// `entry_location` is attached to auth_start / signup_complete / first_checkin so
// you can segment any funnel by the page the user arrived from.

const ENTRY_LOCATION_KEY = 'brrgr_entry_location';

export const setEntryLocation = (location: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(ENTRY_LOCATION_KEY, location);
  } catch {}
};

export const getEntryLocation = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.sessionStorage.getItem(ENTRY_LOCATION_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

export const clearEntryLocation = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ENTRY_LOCATION_KEY);
  } catch {}
};

export const trackSplashView = () => {
  trackEvent('splash_view', 'funnel', 'landing_page');
};

export const trackAuthStart = (method: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'auth_start', {
      event_category: 'funnel',
      event_label: method,
      method,
      entry_location: getEntryLocation() ?? 'direct',
    });
  }
};

export const trackLeaderboardCtaClick = (locationSlug: string) => {
  setEntryLocation(locationSlug || 'world');
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'leaderboard_cta_click', {
      event_category: 'funnel',
      event_label: locationSlug || 'world',
      location: locationSlug || 'world',
    });
  }
};

// Track signup conversion (fires once per user)
export const trackSignupComplete = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'signup_complete', {
      event_category: 'funnel',
      event_label: 'home_landing',
      entry_location: getEntryLocation() ?? 'direct',
    });
  }
};

export const trackFirstCheckin = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'first_checkin', {
      event_category: 'funnel',
      event_label: 'onboarding_complete',
      entry_location: getEntryLocation() ?? 'direct',
    });
  }
  clearEntryLocation();
};

// ── Logged-out homepage conversion funnel ──
// home_view → home_section_view → home_cta_click → auth_start → signup_complete

const getUtmParams = () => {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const out: Record<string, string> = { ...getPersistedUtm() };
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
    const v = p.get(k);
    if (v) out[k] = v;
  });
  return out;
};

export const trackHomeView = () => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'home_view', {
    event_category: 'home_funnel',
    page_referrer: document.referrer || 'direct',
    platform: detectPlatform(),
    ...getUtmParams(),
  });
};

export const trackHomeSectionView = (sectionId: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'home_section_view', {
    event_category: 'home_funnel',
    event_label: sectionId,
    section_id: sectionId,
  });
};

export const trackHomeCtaClick = (ctaId: string, destination?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'home_cta_click', {
    event_category: 'home_funnel',
    event_label: ctaId,
    cta_id: ctaId,
    destination: destination ?? 'unknown',
  });
};

export const trackHomeScrollDepth = (percent: 25 | 50 | 75 | 100) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'home_scroll_depth', {
    event_category: 'home_funnel',
    event_label: `${percent}`,
    value: percent,
  });
};

// ── Post-signup activation funnel events ──
// signup_complete → first_checkin → burger_created → follow_tap (+ engagement)

const fireActivationEvent = (name: string, params: Record<string, any> = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', name, { event_category: 'activation', ...params });
};

export const trackAddBurgerTap = (source: string) => {
  fireActivationEvent('add_burger_tap', { event_label: source, source });
};

export const trackBurgerCreated = (burgerId?: string) => {
  fireActivationEvent('burger_created', { event_label: burgerId ?? 'unknown', burger_id: burgerId });
};

export const trackDiscoverView = () => {
  fireActivationEvent('discover_view', { event_label: 'discover' });
};

export const trackMapOpen = (source: string = 'direct') => {
  fireActivationEvent('map_open', { event_label: source, source });
};

export const trackProfileEdit = (section: string = 'settings') => {
  fireActivationEvent('profile_edit', { event_label: section, section });
};

export const trackFollowTap = (action: 'follow' | 'unfollow', targetUserId?: string) => {
  fireActivationEvent('follow_tap', { event_label: action, action, target_user_id: targetUserId });
};

export const trackLeaderboardView = (locationSlug: string = 'world') => {
  fireActivationEvent('leaderboard_view', { event_label: locationSlug, location: locationSlug });
};

